import { apiClient } from './client';

function normalizeCourseList(data) {
  return Array.isArray(data) ? data : data?.data || data?.courses || [];
}

// apiClient's response interceptor (src/api/client.js) rewrites rejected
// errors into a plain { message, code, ...data } object — the original
// axios error's `.response` is gone by the time it reaches a caller's catch,
// so the HTTP status must be read from `.code`, not `.response.status`.
function statusOf(err) {
  return err?.code ?? err?.response?.status;
}

function rethrowAsForbidden() {
  // 403 means the teacher role is not authorised — surface as an error
  // rather than silently falling back to the public course list.
  const error = new Error('You do not have permission to access this resource.');
  error.status = 403;
  throw error;
}

// apiClient serves GETs from a cache for minutes. The student's progress and a quiz attempt are
// read right after a write (giving or editing feedback), and their "Refresh" has to show the
// current state, so those requests carry a throwaway param that makes each one unique.
const uncached = () => ({ _: Date.now() });

// GET /teacher/courses returns 20 courses unless asked for more, and at most 100 per request.
const COURSE_PAGE_SIZE = 100;
const MAX_COURSE_PAGES = 20; // 2000 courses — a stop for a backend that ignores `offset`

/**
 * Get teacher's assigned courses.
 *
 * Tries GET /teacher/courses first — a teacher-scoped endpoint (courses the
 * teacher is assigned to or created; ADMIN gets all) that a backend agent is
 * adding concurrently with this fix. Until that endpoint is deployed it 404s,
 * so this falls back to GET /courses/admin (an unfiltered admin listing that
 * TEACHER role is also allowed to call) so the dropdown keeps working before
 * and after the backend deploy. Returns an array of course objects normalized
 * to { id, name, enrollmentCount }. A 403 from either call throws so the
 * caller can show an error state instead of silently falling back further.
 *
 * Every course is returned, not just the first page: the pickers built on this have no
 * "load more", so the pages are walked with limit/offset (the endpoint takes no `page`).
 *
 * @param {object} params extra query params for the request
 */
export async function getTeacherCourses(params = {}) {
  try {
    const courses = [];
    for (let pageNo = 0; pageNo < MAX_COURSE_PAGES; pageNo += 1) {
      const { data } = await apiClient.get('/teacher/courses', {
        params: { ...params, limit: COURSE_PAGE_SIZE, offset: pageNo * COURSE_PAGE_SIZE },
      });
      const page = normalizeCourseList(data);
      courses.push(...page);
      const total = data?.meta?.total;
      if (page.length < COURSE_PAGE_SIZE || (typeof total === 'number' && courses.length >= total)) {
        break;
      }
    }
    return courses;
  } catch (err) {
    if (statusOf(err) === 404) {
      try {
        const { data } = await apiClient.get('/courses/admin', {
          params: { limit: COURSE_PAGE_SIZE, ...params },
        });
        return normalizeCourseList(data);
      } catch (fallbackErr) {
        if (statusOf(fallbackErr) === 403) rethrowAsForbidden();
        throw fallbackErr;
      }
    }
    if (statusOf(err) === 403) rethrowAsForbidden();
    throw err;
  }
}

/**
 * Get students for a specific teacher course.
 * Calls GET /teacher/courses/:courseId/students
 * Backend returns { data: StudentListItemDto[], meta: { total, limit, offset, courseId } }
 *
 * @param {string} courseId
 * @param {{ page?, limit?, search?, status? }} params
 */
export async function getTeacherCourseStudents(courseId, params = {}) {
  const { page, limit = 20, ...rest } = params;
  const offset = page ? (page - 1) * limit : 0;

  const { data } = await apiClient.get(`/teacher/courses/${courseId}/students`, {
    params: { offset, limit, ...rest }
  });

  // Compute totalPages for frontend convenience
  if (data && data.meta) {
    data.meta.totalPages = Math.max(1, Math.ceil(data.meta.total / (limit || 20)));
  }

  return data;
}

/**
 * Get individual student progress in a course.
 * Calls GET /teacher/courses/:courseId/students/:studentId
 * Backend returns StudentProgressDetailDto.
 *
 * @param {string} courseId
 * @param {string} studentId
 */
export async function getTeacherStudentProgress(courseId, studentId) {
  const { data } = await apiClient.get(`/teacher/courses/${courseId}/students/${studentId}`, {
    params: uncached(),
  });
  return data;
}

/**
 * Get teacher effectiveness metrics for a course.
 * Calls GET /teacher/courses/:courseId/metrics
 * Backend returns TeacherMetricsDto:
 *   { courseId, avgCompletionRate, avgQuizScore, activeLast7Days, totalAssigned, atRiskCount, feedbackGiven }
 *
 * @param {string} courseId
 */
export async function getTeacherMetrics(courseId) {
  const { data } = await apiClient.get(`/teacher/courses/${courseId}/metrics`);
  return data;
}

/**
 * Submit feedback on a quiz attempt. Submitting again for the same attempt replaces the
 * feedback given before, so this also edits.
 * Calls POST /teacher/courses/:courseId/students/:studentId/quizzes/:attemptId/feedback
 * Body: { content, suggestions?, rating? } — rating is a 1-5 whole number, not a score.
 *
 * @param {string} courseId
 * @param {string} studentId
 * @param {string} attemptId
 * @param {object} body - { content, suggestions?, rating? }
 */
export async function submitQuizFeedback(courseId, studentId, attemptId, body) {
  const { data } = await apiClient.post(
    `/teacher/courses/${courseId}/students/${studentId}/quizzes/${attemptId}/feedback`,
    body
  );
  return data;
}

/**
 * Get quiz attempt details for the feedback form: the attempt with its answers, the questions it
 * was graded against, its quiz (pass mark, lesson) and any feedback already given. See
 * utils/quizAttempt.js for turning it into something to show.
 * Calls GET /quizzes/attempts/:attemptId
 *
 * @param {string} attemptId
 */
export async function getQuizAttemptDetail(attemptId) {
  const { data } = await apiClient.get(`/quizzes/attempts/${attemptId}`, { params: uncached() });
  return data;
}

/**
 * Send a direct message to a student (used by at-risk Send Message CTA).
 * Calls POST /messages/direct
 *
 * @param {string} studentId
 * @param {object} body - { message, courseId?, type? }
 */
export async function sendStudentMessage(studentId, body) {
  const { data } = await apiClient.post(`/messages/direct`, {
    recipientId: studentId,
    courseId: body.courseId,
    content: body.message || body.content || body.body,
  });
  return data;
}

/**
 * @deprecated Use GET /notifications filtered by AT_RISK types instead.
 * Kept for backward compatibility — not used by AtRiskDashboard v2.
 */
export async function getAtRiskStudents(params = {}) {
  const { data } = await apiClient.get('/teacher/at-risk', { params });
  return data;
}
